import { useEffect, useRef, useState } from 'react'
import { StarTooltip } from '../tooltips/Tooltip'
import FloatingLabel from 'react-bootstrap/FloatingLabel'
import Form from 'react-bootstrap/Form'

/**
 * Un campo di input customizzabile per il tema B5.
 *
 * @see https://react-bootstrap.netlify.app/docs/forms/floating-labels
 *
 * @param {string} label - label che appare dentro la select e che si sposta in alto quando cliccata
 * @param {string} type - Il tipo del campo di input.
 * @param {ReactElement} Icon - Il componente icona visualizzato accanto al campo di input.
 * @param {Function} onIconClick - La funzione che viene chiamata quando l'icona viene cliccata.
 * @param {boolean} clickableIcon - Se vero, l'icona è cliccabile.
 * @param {string} iconTip - Il testo del tooltip per l'icona.
 * @param {string} placeholder - Il testo segnaposto per il campo di input.
 * @param {string} className - Il nome della classe per il campo di input.
 * @param {Function} onChange - La funzione che viene chiamata quando il campo di input viene modificato.
 * @param {string} name - Il nome del campo di input.
 * @param {any} value - Il valore del campo di input.
 * @param {Function} onKeyPress - La funzione che viene chiamata quando viene premuto il tasto Invio.
 * @param {Function} onFocus - La funzione che viene chiamata quando il campo di input viene messo a fuoco.
 * @param {any} maxLength - La lunghezza massima consentita per il campo di input.
 * @param {boolean} showErrorMessage - Se true, il componente visualizza il messaggio di errore.
 * @param {any} error - Messaggio di errore
 * @param {boolean} touched - Se il campo di input e' stato touchato.
 * @param {string} pattern - Il pattern utilizzato per convalidare il testo di input.
 * @param {number} step - L'incremento per il campo di input.
 * @param {Function} onBlur - La funzione che viene chiamata quando il campo di input perde il focus.
 * @param {boolean} readOnly - Se vero, il campo di input è in sola lettura.
 * @param {string} id - L'id del campo di input.
 * @param {Function} onInput - La funzione che viene chiamata quando il campo di input viene modificato.
 * @param {boolean} disabled - Se vero, il campo di input è disabilitato.
 * @param {string} autoComplete - Il valore per l'attributo autocomplete del campo di input.
 * @param {string} autocorrect - Il valore per l'attributo autocorrect del campo di input.
 * @param {boolean} autofocus - Se vero, il campo di input prende il focus automatico al caricamento.
 *  *
 */
export const BwmInput = ({
  label,
  type,
  Icon,
  onIconClick = () => undefined,
  iconTip,
  className,
  onChange,
  name,
  value,
  onKeyPress,
  onFocus,
  maxLength,
  showErrorMessage = false,
  error,
  touched,
  pattern,
  placeholder,
  step,
  onBlur = () => {},
  readOnly,
  id,
  onInput,
  disabled,
  autoComplete,
  autocorrect,
  autofocus = false
}) => {
  /**
   * in certi casi, il cursore può saltare alla fine. questo stato riporta il cursore al posto giusto
   */
  const [cursor, setCursor] = useState(null)
  const ref = useRef(null)

  /**
   * Focus su primo render, se richiesto
   */
  useEffect(() => {
    if (autofocus) {
      ref.current.focus()
    }
  }, [])

  /**
   * in certi casi, il cursore può saltare alla fine. questo stato riporta il cursore al posto giusto
   */
  useEffect(() => {
    const input = ref.current
    if (input && ['text', 'search', 'URL', 'tel', 'password'].includes(input.type))
      input.setSelectionRange(cursor || 0, cursor || 0)
  }, [ref, cursor, value])

  /**
   * Gestisce l'evento di modifica del campo di input.
   * Aggiorna la posizione del cursore e chiama la callback onChange se fornita.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - L'evento di modifica generato dal campo di input.
   */
  const handleChange = (e) => {
    setCursor(e.target.selectionStart)
    onChange && onChange(e)
  }

  /**
   * se touched e error non sono nulli, allora il componente mostra la classe css per l'errore
   */
  let inputWrapperClass = `input-wrapper d-flex w-100 flex-column justify-content-center ${touched && error !== undefined ? 'has-error' : ''}`
  if (Icon) inputWrapperClass += ' with-icon'

  /**
   * se è di tipo numerico e quindi l'input deve stare in fondo, viene aggiunta alla label la classe per spostare la label alla destRA
   */
  let labelClassName = ''
  if (className && className.includes('text-end')) {
    labelClassName = 'label-end'
  }

  return (
    <div className={inputWrapperClass}>
      <StarTooltip text={placeholder}>
        <FloatingLabel
          title={label}
          id="floatingLabel"
          className={`w-100 d-flex ${labelClassName}`}
          label={label}>
          <>
            <Form.Control
              key={name}
              ref={ref}
              id={id}
              type={type}
              className={`${className} ${touched && error !== undefined ? 'is-invalid' : ''}`}
              name={name}
              value={value || ''}
              placeholder=" "
              maxLength={maxLength}
              pattern={pattern}
              step={step}
              readOnly={readOnly}
              disabled={disabled}
              autoComplete={autoComplete}
              autoCorrect={autocorrect}
              onKeyDown={onKeyPress}
              onBlur={onBlur}
              onInput={onInput}
              onFocus={onFocus}
              onChange={handleChange}
            />
            {Icon && (
              <div className="input-group-append">
                <StarTooltip text={iconTip}>
                  <div className={`${className} input-group-text py-0 px-1`} onClick={onIconClick}>
                    <Icon />
                  </div>
                </StarTooltip>
              </div>
            )}
          </>
        </FloatingLabel>
      </StarTooltip>

      {showErrorMessage && error !== undefined && !error.isValid && (
        <div className="text-danger">{error.msg}</div>
      )}
    </div>
  )
}
